import { useEffect, useState } from "react";
import { api, API, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { DownloadCloud, Copy, Chrome, FileJson, FileSpreadsheet, Database, Trash2, RefreshCw } from "lucide-react";

export default function Importer() {
  const [settings, setSettings] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [errors, setErrors] = useState([]);
  const [json, setJson] = useState("");
  const [busy, setBusy] = useState(false);

  const loadJobs = () => {
    api.get("/sync/jobs").then((r) => setJobs(r.data));
    api.get("/sync/errors").then((r) => setErrors(r.data));
  };
  useEffect(() => { api.get("/settings/secrets").then((r) => setSettings(r.data)); loadJobs(); }, []);

  const token = settings?.sync_token || "";
  const ingestUrl = `${API}/sync/clients`;

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copiado!"); };

  const importClients = async (clients, source) => {
    setBusy(true);
    try {
      const { data } = await api.post("/sync/clients", { clients, source }, { headers: { "X-Sync-Token": token } });
      toast.success(`Importados: ${data.created} novos, ${data.updated} atualizados`);
      loadJobs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao importar"); }
    setBusy(false);
  };

  const importJson = () => {
    let arr;
    try { arr = JSON.parse(json); } catch { return toast.error("JSON inválido"); }
    if (!Array.isArray(arr)) return toast.error("O JSON deve ser uma lista de clientes");
    importClients(arr, "json");
  };

  const importCsv = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      const lines = reader.result.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 2) return toast.error("CSV vazio");
      const sep = lines[0].includes(";") ? ";" : ",";
      const headers = lines[0].split(sep).map((h) => h.trim().toLowerCase());
      const clients = lines.slice(1).map((l) => {
        const cells = l.split(sep);
        const o = {}; headers.forEach((h, i) => (o[h] = (cells[i] || "").trim())); return o;
      });
      importClients(clients, "csv");
    };
    reader.readAsText(file);
  };

  const seed = async () => { await api.post("/demo/seed"); toast.success("Dados demo gerados"); loadJobs(); };
  const clear = async () => { await api.delete("/demo/clear"); toast.success("Dados demo removidos"); loadJobs(); };

  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight text-slate-100 flex items-center gap-2"><DownloadCloud className="h-7 w-7 text-sky-400" /> Importador & Extensão</h1>
      <p className="text-sm text-slate-400 mt-1 mb-6">Importe os clientes do painel SYSLOG para o CRM paralelo</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#111827] border border-[#1F293D] rounded-xl p-5">
          <h3 className="text-base font-semibold text-slate-200 flex items-center gap-2 mb-3"><Chrome className="h-5 w-5 text-sky-400" /> Extensão do Chrome</h3>
          <p className="text-sm text-slate-400 mb-4">A forma recomendada. Você abre o painel já logado e a extensão lê e envia os dados para o CRM — sem login automático e sem bloqueio do Cloudflare.</p>
          <button type="button" data-testid="download-extension" onClick={async () => {
              const r = await api.get("/extension/download", { responseType: "blob" });
              const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "smart-ti-sync-crm.zip"; a.click();
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600">
            <DownloadCloud className="h-4 w-4" /> Baixar extensão (.zip)
          </button>
          <ol className="text-xs text-slate-400 mt-4 space-y-1.5 list-decimal list-inside">
            <li>Clique com o botão direito no .zip → <b>Extrair tudo...</b> (ex.: para <code className="font-mono text-sky-300">Downloads\smart-ti-sync-crm</code>)</li>
            <li>Acesse <code className="font-mono text-sky-300">chrome://extensions</code></li>
            <li>Ative o <b>Modo do desenvolvedor</b></li>
            <li>Clique em <b>Carregar sem compactação</b> e selecione a pasta onde está o arquivo <code className="font-mono text-sky-300">manifest.json</code></li>
            <li>Abra o painel SYSLOG logado, clique no ícone da extensão e em <b>Sincronizar</b></li>
          </ol>
          <div className="mt-4 p-3 rounded-lg bg-[#0B0F17] border border-[#1F293D] text-xs text-slate-400">
            <b className="text-slate-200">Um único login basta:</b> o login master <code className="font-mono text-sky-300">csalpha</code> enxerga
            os clientes de <code className="font-mono text-sky-300">csalpha</code>, <code className="font-mono text-sky-300">alpha2</code>, <code className="font-mono text-sky-300">wetester</code> e <code className="font-mono text-sky-300">alpha4</code>.
            Deixe <b>uma janela</b> do painel aberta e logada com a sincronização automática a cada <b>5 min</b> (rápida) — a cada 6 h ela faz uma varredura completa.
          </div>
        </div>

        <div className="bg-[#111827] border border-[#1F293D] rounded-xl p-5">
          <h3 className="text-base font-semibold text-slate-200 flex items-center gap-2 mb-3"><Database className="h-5 w-5 text-emerald-400" /> Credenciais de sincronização</h3>
          <p className="text-xs text-slate-400 mb-3">Configure estes valores na extensão (ou use para integrar via API).</p>
          <Field label="URL de ingestão" value={ingestUrl} onCopy={() => copy(ingestUrl)} testid="copy-url" />
          <Field label="Token de sincronização" value={token} onCopy={() => copy(token)} testid="copy-token" />
          <div className="text-[11px] text-slate-500 mt-3">A extensão envia os dados via <code className="font-mono">POST</code> com o header <code className="font-mono text-sky-300">X-Sync-Token</code>.</div>
        </div>

        <div className="bg-[#111827] border border-[#1F293D] rounded-xl p-5">
          <h3 className="text-base font-semibold text-slate-200 flex items-center gap-2 mb-3"><FileJson className="h-5 w-5 text-amber-400" /> Importar JSON</h3>
          <textarea data-testid="json-input" value={json} onChange={(e) => setJson(e.target.value)} rows={5}
            placeholder='[{"nome":"João","email":"joao@x.com","whatsapp":"5511999999999","plano":"IPTV Mensal","valor":35,"vencimento":"2026-10-20","usuario":"joao01"}]'
            className="w-full px-3 py-2 rounded-lg bg-[#0B0F17] border border-[#1F293D] text-xs font-mono text-slate-200 focus:border-sky-500 focus:outline-none" />
          <button data-testid="import-json-button" disabled={busy} onClick={importJson}
            className="mt-3 px-4 py-2 rounded-lg bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 disabled:opacity-50">Importar JSON</button>
        </div>

        <div className="bg-[#111827] border border-[#1F293D] rounded-xl p-5">
          <h3 className="text-base font-semibold text-slate-200 flex items-center gap-2 mb-3"><FileSpreadsheet className="h-5 w-5 text-emerald-400" /> Importar CSV / Dados demo</h3>
          <p className="text-xs text-slate-400 mb-3">Colunas aceitas: nome, email, whatsapp, plano, valor, vencimento, usuario, servidor...</p>
          <label data-testid="csv-input-label" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1A2234] border border-[#202D45] text-slate-200 text-sm cursor-pointer hover:border-[#334155]">
            <FileSpreadsheet className="h-4 w-4" /> Selecionar CSV
            <input data-testid="csv-input" type="file" accept=".csv" className="hidden" onChange={(e) => e.target.files[0] && importCsv(e.target.files[0])} />
          </label>
          <div className="flex gap-2 mt-4 pt-4 border-t border-[#1F293D]">
            <button data-testid="seed-demo" onClick={seed} className="px-3 py-2 rounded-lg bg-[#1A2234] border border-[#202D45] text-slate-300 text-xs hover:text-white">Gerar dados demo</button>
            <button data-testid="clear-demo" onClick={clear} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs hover:bg-red-500/20"><Trash2 className="h-3.5 w-3.5" /> Limpar demo</button>
          </div>
        </div>
      </div>

      {errors.length > 0 && (
        <div data-testid="sync-errors" className="bg-red-500/5 border border-red-500/20 rounded-xl p-5 mt-6">
          <h3 className="text-base font-semibold text-red-300 mb-3">Avisos da extensão</h3>
          {errors.map((e) => (
            <div key={e.id} className="text-xs text-slate-300 mb-2">
              <span className="text-slate-500">{(e.created_at || "").replace("T", " ").slice(0, 16)} · {e.account || "—"} · v{e.version || "?"}</span>
              <div className="text-red-300">{e.error}</div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-[#111827] border border-[#1F293D] rounded-xl p-5 mt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-slate-200">Histórico de sincronizações</h3>
          <button onClick={loadJobs} className="p-2 rounded-lg bg-[#1A2234] border border-[#202D45] text-slate-300 hover:text-white"><RefreshCw className="h-4 w-4" /></button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-[#1F293D]">
              <th className="px-3 py-2 font-medium">Origem</th><th className="px-3 py-2 font-medium">Recebidos</th>
              <th className="px-3 py-2 font-medium">Novos</th><th className="px-3 py-2 font-medium">Atualizados</th>
              <th className="px-3 py-2 font-medium">Quando</th></tr></thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id} className="border-b border-[#1F293D]">
                  <td className="px-3 py-2 text-slate-300">{j.source}</td>
                  <td className="px-3 py-2 font-mono text-slate-400">{j.received}</td>
                  <td className="px-3 py-2 font-mono text-emerald-400">{j.created}</td>
                  <td className="px-3 py-2 font-mono text-sky-400">{j.updated}</td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{(j.created_at || "").replace("T", " ").slice(0, 16)}</td>
                </tr>
              ))}
              {jobs.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">Nenhuma sincronização ainda.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value, onCopy, testid }) => (
  <div className="mb-2">
    <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    <div className="flex items-center gap-2">
      <code className="flex-1 px-3 py-2 rounded-lg bg-[#0B0F17] border border-[#1F293D] text-xs font-mono text-sky-300 truncate">{value}</code>
      <button data-testid={testid} onClick={onCopy} className="p-2 rounded-lg bg-[#1A2234] border border-[#202D45] text-slate-300 hover:text-white"><Copy className="h-4 w-4" /></button>
    </div>
  </div>
);
